import { InlineError } from "@/components/QueryState";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { formatCostLong, formatDate } from "@/lib/format";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useState } from "react";

const PAGE_SIZE = 25;

// Money movements only. Per-request usage rows (model calls, compute
// snapshots, sandbox time) are deliberately excluded — that grain lives on
// the Usage page; here the customer sees what moved money in or out of
// their wallet.
const PAYMENT_TYPES = [
  "purchase",
  "auto_recharge",
  "topup",
  "adjustment",
  "refund",
  "welcome_grant",
  "subscription_grant",
];

// Grants are branded as ours; the specific occasion comes from the row's
// grantLabel ("Boo Lab Credit — Early adopter"), so new campaigns need a
// label at grant time, not a code change here.
const TYPE_LABEL: Record<string, string> = {
  purchase: "Credit purchase",
  auto_recharge: "Auto-recharge",
  topup: "Boo Lab Credit",
  adjustment: "Adjustment",
  refund: "Refund",
  welcome_grant: "Boo Lab Credit",
  subscription_grant: "Boo Lab Credit",
};

const GRANT_TYPES = new Set(["topup", "welcome_grant", "subscription_grant"]);

function typeLabel(row: { type: string; grantLabel?: string | null }): string {
  const base = TYPE_LABEL[row.type] ?? row.type;
  if (GRANT_TYPES.has(row.type) && row.grantLabel) return `${base} \u2014 ${row.grantLabel}`;
  return base;
}

const STATUS_LABEL: Record<string, string> = {
  purchase: "Paid",
  auto_recharge: "Paid",
  topup: "Granted",
  adjustment: "Adjusted",
  refund: "Refunded",
  welcome_grant: "Granted",
  subscription_grant: "Granted",
};

// "Transactions" on the Credit balance tab: a plain balance history. Invoices
// live on their own tab, so rows carry no invoice links.
export function TransactionsSection() {
  const [offset, setOffset] = useState(0);
  const { data, isError, error, isLoading } = useQuery({
    queryKey: ["billing-transactions", offset],
    queryFn: () => api.billing.transactions(PAGE_SIZE, offset, PAYMENT_TYPES),
    placeholderData: keepPreviousData,
  });

  if (isLoading) return null;
  if (isError) {
    return (
      <section className="space-y-4 border-t pt-8">
        <h2 className="text-base font-semibold">Transactions</h2>
        <InlineError error={error ?? "Failed to load transactions"} />
      </section>
    );
  }
  if (!data || data.total === 0) return null;

  return (
    <section className="space-y-4 border-t pt-8">
      <div className="space-y-1">
        <h2 className="text-base font-semibold">Transactions</h2>
        <p className="text-sm text-muted-foreground">
          Every purchase, auto-recharge, credit grant and adjustment to your
          balance. Invoices are on the{" "}
          <Link
            to="."
            search={{ tab: "invoices" } as never}
            className="text-primary underline underline-offset-4 hover:no-underline"
          >
            Invoices tab
          </Link>
          .
        </p>
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b text-left text-muted-foreground">
            <th className="py-2 pr-3 font-medium">Date</th>
            <th className="py-2 pr-3 font-medium">What happened</th>
            <th className="py-2 pr-3 font-medium text-right">Amount</th>
            <th className="py-2 pl-8 font-medium">Status</th>
          </tr>
        </thead>
        <tbody className="divide-y">
          {data.entries.map((row) => (
            <tr key={row.id}>
              <td className="py-3 pr-3 whitespace-nowrap">{formatDate(row.createdAt)}</td>
              <td className="py-3 pr-3">
                {typeLabel(row)}
                {row.type === "adjustment" && row.description ? (
                  <span className="block text-xs text-muted-foreground">{row.description}</span>
                ) : null}
              </td>
              <td
                className={`py-3 pr-3 text-right tabular-nums ${
                  row.amount < 0 ? "text-destructive" : ""
                }`}
              >
                {row.amount > 0 ? "+" : ""}
                {formatCostLong(row.amount)}
              </td>
              <td className="py-3 pl-8 text-muted-foreground">{STATUS_LABEL[row.type] ?? "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {data.total > PAGE_SIZE && (
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span className="tabular-nums">
            {data.offset + 1}–{Math.min(data.offset + data.entries.length, data.total)} of{" "}
            {data.total}
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
              disabled={data.offset === 0}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setOffset((o) => o + PAGE_SIZE)}
              disabled={data.offset + data.entries.length >= data.total}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}
